import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import DiscussionBoardService, {
  BlogPostBean,
  CommentBean,
  extractErrorMessage,
} from "../../_services/DiscussionBoardService";
import "./discussion-board.css";

const initials = (name: string): string => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
};

interface ConfirmState {
  show: boolean;
  message: string;
  onConfirm: () => Promise<void>;
}

const DiscussionBoardTopicPage: React.FC = () => {
  const history = useHistory();
  const { boardId, topicId } = useParams<{ boardId: string; topicId: string }>();
  const [topic, setTopic] = useState<BlogPostBean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddComment, setShowAddComment] = useState(false);
  const [draftComment, setDraftComment] = useState("");
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
  const [draftReply, setDraftReply] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState>({
    show: false,
    message: "",
    onConfirm: async () => undefined,
  });

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    DiscussionBoardService.getTopic(topicId)
      .then((res) => setTopic(res.data))
      .catch((e) => setError(extractErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [topicId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const submitComment = async () => {
    if (!draftComment.trim()) return;
    setSubmitting(true);
    try {
      await DiscussionBoardService.addComment({ topicId, comment: draftComment });
      setDraftComment("");
      setShowAddComment(false);
      reload();
    } catch (e: any) {
      setError(extractErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async (commentId: string) => {
    if (!draftReply.trim()) return;
    setSubmitting(true);
    try {
      await DiscussionBoardService.addReply({
        id: commentId,
        postId: topicId,
        replyContent: draftReply,
      });
      setDraftReply("");
      setReplyToCommentId(null);
      reload();
    } catch (e: any) {
      setError(extractErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const askDelete = (id: string, type: "topic" | "comment" | "reply") => {
    setConfirm({
      show: true,
      message:
        type === "topic"
          ? "Are you sure you want to delete this topic? This action cannot be undone."
          : `Delete this ${type}? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await DiscussionBoardService.deleteTopicOrComment({ id, type });
          setConfirm((c) => ({ ...c, show: false }));
          if (type === "topic") history.push(`/discussion-board/${boardId}`);
          else reload();
        } catch (e: any) {
          setError(extractErrorMessage(e));
        }
      },
    });
  };

  const askTogglePin = () => {
    if (!topic) return;
    const isPinned = Number(topic.sticky) > 0;
    setConfirm({
      show: true,
      message: isPinned ? "Unpin this topic from the top?" : "Pin this topic to the top of the list?",
      onConfirm: async () => {
        try {
          await DiscussionBoardService.togglePin({ id: topicId });
          setConfirm((c) => ({ ...c, show: false }));
          reload();
        } catch (e: any) {
          setError(extractErrorMessage(e));
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="dbt-page">
        <div className="dbt-topic-header" style={{ opacity: 0.7 }}>
          <div className="dbt-topic-header-body">
            <div className="dbt-breadcrumb">Discussion Board · Topic</div>
            <h1 className="dbt-topic-header-title">Loading…</h1>
          </div>
        </div>
        <div className="dbt-post" style={{ marginTop: 18 }}>
          <div className="dbt-skeleton" style={{ border: "none", borderRadius: 0 }}>
            <div className="dbt-skeleton-avatar" />
            <div style={{ flex: 1 }}>
              <div className="dbt-skeleton-line short" />
              <div className="dbt-skeleton-line shorter" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dbt-page">
        <div className="dbt-error">
          <div className="dbt-error-icon">!</div>
          <h3 className="dbt-error-title">Couldn't load the topic</h3>
          <p className="dbt-error-text">{error}</p>
          <Button
            variant="primary"
            onClick={reload}
            style={{ marginTop: 18, borderRadius: 10, padding: "8px 20px", fontWeight: 500 }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!topic) return null;

  const isPinned = Number(topic.sticky) > 0;
  const authorName = topic.userName || "Unknown";

  return (
    <div className="dbt-page">
      {/* Header */}
      <div className="dbt-topic-header">
        <Button
          className="dbt-back-btn"
          onClick={() => history.push(`/discussion-board/${boardId}`)}
        >
          ← Back
        </Button>
        <div className="dbt-topic-header-body">
          <div className="dbt-breadcrumb">Discussion Board · Topic</div>
          <h1 className="dbt-topic-header-title">
            {topic.title}
            {isPinned && <span className="dbt-pinned-banner">📌 Pinned</span>}
          </h1>
        </div>
        {topic.moderatorPermission && (
          <div className="dbt-topic-header-actions">
            <Button className="dbt-action-btn" onClick={askTogglePin}>
              {isPinned ? "Unpin" : "Pin"}
            </Button>
            <Button
              className="dbt-action-btn dbt-action-btn-danger"
              onClick={() => askDelete(topicId, "topic")}
            >
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Original post */}
      <div className="dbt-post">
        <div className="dbt-post-author">
          {topic.userImagePath ? (
            <img
              src={topic.userImagePath}
              alt={authorName}
              className="dbt-author-avatar"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
                const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
                if (fallback) fallback.style.display = "inline-flex";
              }}
            />
          ) : null}
          <span
            className="dbt-author-avatar-fallback"
            style={{ display: topic.userImagePath ? "none" : "inline-flex" }}
          >
            {initials(authorName)}
          </span>
          <div className="dbt-author-info">
            <p className="dbt-author-name">{authorName || "—"}</p>
            <p className="dbt-author-time">{topic.postedon}</p>
          </div>
        </div>
        <div
          className="dbt-post-content"
          dangerouslySetInnerHTML={{ __html: topic.content }}
        />
      </div>

      {/* Comments */}
      <div className="dbt-comments">
        <div className="dbt-comments-header">
          <span className="dbt-comments-count-badge">{topic.comments.length}</span>
          <h4 className="dbt-comments-title">
            {topic.comments.length === 1 ? "Comment" : "Comments"}
          </h4>
          {topic.commentPermission && (
            <Button
              className="dbt-leave-comment-btn"
              onClick={() => setShowAddComment(true)}
            >
              + Leave a comment
            </Button>
          )}
        </div>

        <div className="dbt-comments-body">
          {topic.comments.length === 0 ? (
            <div className="dbt-comments-empty">
              <span className="dbt-comments-empty-icon">💭</span>
              No comments yet. Be the first to share your thoughts.
            </div>
          ) : (
            topic.comments.map((c) => (
              <CommentRow
                key={c.id}
                comment={c}
                moderatorPermission={topic.moderatorPermission}
                commentPermission={topic.commentPermission}
                replyToCommentId={replyToCommentId}
                draftReply={draftReply}
                submitting={submitting}
                onAskReply={(id) => {
                  setReplyToCommentId(id);
                  setDraftReply("");
                }}
                onCancelReply={() => {
                  setReplyToCommentId(null);
                  setDraftReply("");
                }}
                onChangeReply={setDraftReply}
                onSubmitReply={submitReply}
                onAskDelete={askDelete}
              />
            ))
          )}
        </div>
      </div>

      {/* New comment modal */}
      <Modal
        show={showAddComment}
        onHide={() => setShowAddComment(false)}
        size="lg"
        centered
        dialogClassName="dbt-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Leave a comment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label className="dbt-modal-label">Your comment</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              className="dbt-modal-textarea"
              value={draftComment}
              onChange={(e) => setDraftComment(e.target.value)}
              placeholder="Share your thoughts on this topic…"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAddComment(false)}
            disabled={submitting}
            style={{ borderRadius: 10, padding: "8px 18px", fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submitComment}
            disabled={submitting || !draftComment.trim()}
            style={{ borderRadius: 10, padding: "8px 22px", fontWeight: 500 }}
          >
            {submitting ? "Posting…" : "Post Comment"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm modal */}
      <Modal
        show={confirm.show}
        onHide={() => setConfirm((c) => ({ ...c, show: false }))}
        centered
        dialogClassName="dbt-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Please confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.95rem", color: "var(--text-strong)" }}>
          {confirm.message}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setConfirm((c) => ({ ...c, show: false }))}
            style={{ borderRadius: 10, padding: "8px 18px", fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => confirm.onConfirm()}
            style={{ borderRadius: 10, padding: "8px 22px", fontWeight: 500 }}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

interface CommentRowProps {
  comment: CommentBean;
  moderatorPermission: boolean;
  commentPermission: boolean;
  replyToCommentId: string | null;
  draftReply: string;
  submitting: boolean;
  onAskReply: (commentId: string) => void;
  onCancelReply: () => void;
  onChangeReply: (v: string) => void;
  onSubmitReply: (commentId: string) => Promise<void>;
  onAskDelete: (id: string, type: "comment" | "reply") => void;
}

const CommentRow: React.FC<CommentRowProps> = ({
  comment,
  moderatorPermission,
  commentPermission,
  replyToCommentId,
  draftReply,
  submitting,
  onAskReply,
  onCancelReply,
  onChangeReply,
  onSubmitReply,
  onAskDelete,
}) => {
  const replying = replyToCommentId === comment.id;
  const authorName = comment.createdBy || "Unknown";

  return (
    <div className="dbt-comment">
      {comment.userImagePath ? (
        <img
          src={comment.userImagePath}
          alt={authorName}
          className="dbt-comment-avatar"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
            const fb = e.currentTarget.nextElementSibling as HTMLElement | null;
            if (fb) fb.style.display = "inline-flex";
          }}
        />
      ) : null}
      <span
        className="dbt-comment-avatar-fallback"
        style={{ display: comment.userImagePath ? "none" : "inline-flex" }}
      >
        {initials(authorName)}
      </span>

      <div className="dbt-comment-body">
        <div className="dbt-comment-head">
          <span className="dbt-comment-author">{authorName}</span>
          <span className="dbt-comment-time">{comment.createdOn}</span>
          {moderatorPermission && (
            <div className="dbt-comment-actions">
              <Button
                className="dbt-link-btn dbt-link-btn-danger"
                onClick={() => onAskDelete(comment.id, "comment")}
              >
                Delete
              </Button>
            </div>
          )}
        </div>

        <div
          className="dbt-comment-text"
          dangerouslySetInnerHTML={{ __html: comment.content }}
        />

        {commentPermission && comment.statusId !== "0" && !replying && (
          <div className="dbt-reply-btn-row">
            <Button className="dbt-link-btn" onClick={() => onAskReply(comment.id)}>
              ↳ Reply
            </Button>
          </div>
        )}

        {replying && (
          <div className="dbt-composer">
            <Form.Control
              as="textarea"
              rows={3}
              className="dbt-composer-textarea"
              value={draftReply}
              onChange={(e) => onChangeReply(e.target.value)}
              placeholder="Write your reply…"
              autoFocus
            />
            <div className="dbt-composer-actions">
              <Button
                className="dbt-composer-cancel"
                onClick={onCancelReply}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                className="dbt-composer-submit"
                onClick={() => onSubmitReply(comment.id)}
                disabled={submitting || !draftReply.trim()}
              >
                {submitting ? "Posting…" : "Reply"}
              </Button>
            </div>
          </div>
        )}

        {comment.replies.length > 0 && (
          <div className="dbt-replies">
            {comment.replies.map((r) => {
              const replyAuthor = r.createdBy || "Unknown";
              return (
                <div key={r.id} className="dbt-reply">
                  {r.userImagePath ? (
                    <img
                      src={r.userImagePath}
                      alt={replyAuthor}
                      className="dbt-reply-avatar"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                        const fb = e.currentTarget.nextElementSibling as HTMLElement | null;
                        if (fb) fb.style.display = "inline-flex";
                      }}
                    />
                  ) : null}
                  <span
                    className="dbt-reply-avatar-fallback"
                    style={{ display: r.userImagePath ? "none" : "inline-flex" }}
                  >
                    {initials(replyAuthor)}
                  </span>
                  <div className="dbt-reply-body">
                    <div className="dbt-reply-head">
                      <span className="dbt-reply-author">{replyAuthor}</span>
                      <span className="dbt-reply-time">{r.createdOn}</span>
                      {moderatorPermission && (
                        <div className="dbt-comment-actions">
                          <Button
                            className="dbt-link-btn dbt-link-btn-danger"
                            onClick={() => onAskDelete(r.id, "reply")}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                    <div
                      className="dbt-reply-text"
                      dangerouslySetInnerHTML={{ __html: r.content }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscussionBoardTopicPage;
